/**
 * Board Process Digitisation - Saarv BoardOS
 * Phase 1A: Meeting Creation & Agenda Builder
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Calendar, Clock, MapPin, Users, Plus, Edit2, Trash2,
  FileText, CheckCircle, AlertCircle, ArrowRight, Video, Activity, Play,
  BookOpen, ListChecks, Sparkles
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BoardProcessModule = ({ user }) => {
  const { showSuccess, showError, showWarning, confirm } = useAlert();
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('meetings'); // 'meetings' or 'meeting-detail'

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/boardos/meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(res.data);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMeetingTypeColor = (type) => {
    if (type === 'Quarterly') return 'bg-blue-100 text-blue-700 border-blue-300';
    if (type === 'Annual') return 'bg-purple-100 text-purple-700 border-purple-300';
    return 'bg-orange-100 text-orange-700 border-orange-300';
  };

  const getStatusColor = (status) => {
    if (status === 'Completed') return 'bg-green-100 text-green-700';
    if (status === 'In Progress') return 'bg-yellow-100 text-yellow-700';
    if (status === 'Scheduled') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (selectedMeeting) {
    return <MeetingDetail meeting={selectedMeeting} onBack={() => setSelectedMeeting(null)} user={user} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Board Meetings</h2>
          <p className="text-gray-600 mt-1">Prepare → Decide → Record → Execute → Track → Learn</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Meeting
        </button>
      </div>

      {/* Meetings Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading meetings...</p>
        </div>
      ) : meetings.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Board Meetings Yet</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first board meeting</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create First Meeting
          </button>
        </div>
      ) : (
        // Group meetings by fiscal year
        (() => {
          const meetingsByYear = {};
          meetings.forEach(meeting => {
            const year = new Date(meeting.meeting_date).getFullYear();
            const fy = `FY ${year}-${year + 1}`;
            if (!meetingsByYear[fy]) meetingsByYear[fy] = [];
            meetingsByYear[fy].push(meeting);
          });

          // Sort years descending
          const sortedYears = Object.keys(meetingsByYear).sort().reverse();

          return (
            <div className="space-y-6">
              {sortedYears.map(fy => (
                <div key={fy}>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                    {fy}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {meetingsByYear[fy].map((meeting) => (
            <div
              key={meeting.id}
              onClick={() => setSelectedMeeting(meeting)}
              className="bg-white rounded-xl border-2 border-gray-200 p-5 hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getMeetingTypeColor(meeting.meeting_type)}`}>
                    {meeting.meeting_type}
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(meeting.status)}`}>
                  {meeting.status}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-900">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <span className="font-semibold">
                    {new Date(meeting.meeting_date).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{meeting.meeting_time}</span>
                </div>

                {meeting.location && (
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{meeting.location}</span>
                  </div>
                )}

                {meeting.virtual_link && (
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <Video className="w-4 h-4" />
                    <span className="text-indigo-600">Virtual Meeting</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Users className="w-4 h-4" />
                  <span>{meeting.attendees?.length || 0} attendees</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FileText className="w-4 h-4" />
                  <span>{meeting.agenda_count || 0} agenda items</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button className="text-sm text-indigo-600 font-medium flex items-center gap-1 hover:text-indigo-700">
                  Open Meeting <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}

      {/* Create Meeting Modal */}
      {showCreateForm && (
        <CreateMeetingModal
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => { setShowCreateForm(false); fetchMeetings(); }}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />
      )}
    </div>
  );
};

// ==================== CREATE MEETING MODAL ====================
const CreateMeetingModal = ({ onClose, onSuccess, showSuccess, showError, showWarning }) => {
  const [formData, setFormData] = useState({
    meeting_type: 'Quarterly',
    meeting_date: '',
    meeting_time: '',
    location: '',
    virtual_link: '',
    attendees: []
  });
  const [saving, setSaving] = useState(false);
  const [attendeeEmail, setAttendeeEmail] = useState('');

  const handleSubmit = async () => {
    if (!formData.meeting_date || !formData.meeting_time) {
      showWarning('Please fill in meeting date and time');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/boardos/meetings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Meeting created successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Create New Board Meeting</h3>
          <button onClick={onClose} className="text-white hover:bg-indigo-700 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type *</label>
            <select
              value={formData.meeting_type}
              onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Quarterly">Quarterly Board Meeting</option>
              <option value="Annual">Annual General Meeting</option>
              <option value="Special">Special Board Meeting</option>
              <option value="Committee">Committee Meeting</option>
              <option value="Audit Committee">Audit Committee Meeting</option>
              <option value="Nomination and Remuneration">Nomination and Remuneration Committee Meeting</option>
              <option value="Risk Management">Risk Management Committee Meeting</option>
              <option value="Stakeholders Relationship">Stakeholders Relationship Committee Meeting</option>
              <option value="CSR">Corporate Social Responsibility Committee Meeting</option>
              <option value="Internal Complaints">Internal Complaints Committee Meeting</option>
              <option value="Disclosure">Disclosure/Compliance Committee Meeting</option>
              <option value="ESG">ESG/Sustainability Committee</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Date *</label>
              <input
                type="date"
                value={formData.meeting_date}
                onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Time *</label>
              <input
                type="time"
                value={formData.meeting_time}
                onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Board Room, Conference Center"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Virtual Meeting Link</label>
            <input
              type="url"
              value={formData.virtual_link}
              onChange={(e) => setFormData({ ...formData, virtual_link: e.target.value })}
              placeholder="e.g., Zoom, Teams link"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Attendees (Board Members)</label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={attendeeEmail}
                  onChange={(e) => setAttendeeEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (attendeeEmail && !formData.attendees.includes(attendeeEmail)) {
                        setFormData({ ...formData, attendees: [...formData.attendees, attendeeEmail] });
                        setAttendeeEmail('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (attendeeEmail && !formData.attendees.includes(attendeeEmail)) {
                      setFormData({ ...formData, attendees: [...formData.attendees, attendeeEmail] });
                      setAttendeeEmail('');
                    }
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
              {formData.attendees.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.attendees.map((email, i) => (
                    <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-2">
                      {email}
                      <button
                        onClick={() => setFormData({ ...formData, attendees: formData.attendees.filter((_, idx) => idx !== i) })}
                        className="text-indigo-900 hover:text-indigo-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> After creating the meeting, you&apos;ll be able to add agenda items, upload documents, and invite attendees.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Creating...' : 'Create Meeting'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MEETING DETAIL VIEW ====================
const MeetingDetail = ({ meeting, onBack, user }) => {
  const { showSuccess, showError, showWarning, confirm } = useAlert();
  const [meetingData, setMeetingData] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [showAddAgenda, setShowAddAgenda] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agenda'); // 'agenda' or 'readiness' or 'live' or 'minutes' or 'actions'
  const [readinessData, setReadinessData] = useState(null);
  const [currentAgendaIndex, setCurrentAgendaIndex] = useState(0);
  const [minutes, setMinutes] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [generatingMinutes, setGeneratingMinutes] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [showAddAction, setShowAddAction] = useState(false);
  const [showTranscriptText, setShowTranscriptText] = useState(false);

  const getStatusColor = (status) => {
    if (status === 'Completed') return 'bg-green-100 text-green-700';
    if (status === 'In Progress') return 'bg-yellow-100 text-yellow-700';
    if (status === 'Scheduled') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  useEffect(() => {
    fetchMeetingDetails();
  }, [meeting.id]);

  const fetchMeetingDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const [meetingRes, readinessRes, minutesRes, actionsRes, transcriptRes] = await Promise.all([
        axios.get(`${API}/api/boardos/meetings/${meeting.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/boardos/meetings/${meeting.id}/readiness`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/boardos/meetings/${meeting.id}/minutes`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/api/boardos/action-items?meeting_id=${meeting.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/boardos/meetings/${meeting.id}/transcript`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setMeetingData(meetingRes.data);
      setAgendaItems(meetingRes.data.agenda_items || []);
      setReadinessData(readinessRes.data);
      setMinutes(minutesRes.data);
      setActionItems(actionsRes.data);
      setTranscript(transcriptRes.data);
      
      // Find current agenda index
      const currentIndex = meetingRes.data.agenda_items?.findIndex(item => item.status === 'Current');
      if (currentIndex >= 0) {
        setCurrentAgendaIndex(currentIndex);
      }
    } catch (error) {
      console.error('Error fetching meeting details:', error);
    } finally {
      setLoading(false);
    }
  };

  const startMeeting = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/boardos/meetings/${meeting.id}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveTab('live');
      fetchMeetingDetails();
    } catch (error) {
      showError('Error starting meeting: ' + error.message);
    }
  };

  const markCurrentComplete = async (actualDuration) => {
    const currentItem = agendaItems[currentAgendaIndex];
    if (!currentItem) return;

    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/boardos/agenda-items/${currentItem.id}/mark-complete?actual_duration=${actualDuration}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeetingDetails();
    } catch (error) {
      showError('Error: ' + error.message);
    }
  };

  const deferCurrentItem = async () => {
    const currentItem = agendaItems[currentAgendaIndex];
    if (!currentItem) return;

    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/boardos/agenda-items/${currentItem.id}/defer`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMeetingDetails();
    } catch (error) {
      showError('Error: ' + error.message);
    }
  };

  const generateMinutes = async () => {
    setGeneratingMinutes(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/boardos/meetings/${meeting.id}/generate-minutes`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMinutes(res.data);
      setActiveTab('minutes');
      showSuccess('Draft minutes generated successfully!');
    } catch (error) {
      showError('Error generating minutes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingMinutes(false);
    }
  };

  const handleRecordingUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const confirmed = await confirm(
      `Upload recording "${file.name}"? This will be transcribed using AI (may take 1-2 minutes for long recordings).`
    );
    if (!confirmed) return;

    setUploadingRecording(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(
        `${API}/api/boardos/meetings/${meeting.id}/transcribe-audio`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      showSuccess(`Recording transcribed successfully! Transcript length: ${res.data.transcript_text.length} characters`);
      setTranscript(res.data);
      fetchMeetingDetails();
    } catch (error) {
      showError('Transcription error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingRecording(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-gray-600">Loading meeting details...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <span>←</span> Back to Meetings
      </button>

      {/* Meeting Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
              {meetingData.meeting_type} Board Meeting
            </span>
            <h2 className="text-3xl font-bold mt-3">{meetingData.meeting_type} Board Meeting</h2>
          </div>
          <span className={`px-4 py-2 rounded-lg font-medium ${getStatusColor(meetingData.status)}`}>
            {meetingData.status}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <div>
              <p className="text-xs text-indigo-100">Date</p>
              <p className="font-semibold">
                {new Date(meetingData.meeting_date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <div>
              <p className="text-xs text-indigo-100">Time</p>
              <p className="font-semibold">{meetingData.meeting_time}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            <div>
              <p className="text-xs text-indigo-100">Attendees</p>
              <p className="font-semibold">{meetingData.attendees?.length || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <div>
              <p className="text-xs text-indigo-100">Agenda Items</p>
              <p className="font-semibold">{agendaItems.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-2 shadow-sm">
        <div className="flex gap-1 items-center justify-between flex-wrap">
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setActiveTab('agenda')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'agenda' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Agenda Builder
              </span>
            </button>
            <button
              onClick={() => setActiveTab('readiness')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'readiness' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Pre-Board Readiness
              </span>
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'live' 
                  ? 'bg-green-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
              disabled={meetingData.status === 'Draft'}
            >
              <span className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Live Meeting
              </span>
            </button>
            <button
              onClick={() => setActiveTab('minutes')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'minutes' 
                  ? 'bg-purple-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Minutes
              </span>
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                activeTab === 'actions' 
                  ? 'bg-orange-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2">
                <ListChecks className="w-4 h-4" />
                Action Items
              </span>
            </button>
          </div>
          
          <div className="flex gap-2">
            {meetingData.status === 'Draft' && activeTab !== 'live' && (
              <button
                onClick={startMeeting}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Start Meeting
              </button>
            )}
            {meetingData.status === 'In Progress' && activeTab === 'minutes' && !minutes && (
              <button
                onClick={generateMinutes}
                disabled={generatingMinutes}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {generatingMinutes ? 'Generating...' : 'Generate Minutes (AI)'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Agenda Builder */}
      {activeTab === 'agenda' && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Meeting Agenda</h3>
          <button
            onClick={() => setShowAddAgenda(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Agenda Item
          </button>
        </div>

        {agendaItems.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No agenda items yet</p>
            <p className="text-sm text-gray-500 mt-1">Add items to build the meeting agenda</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agendaItems.map((item, index) => (
              <AgendaTile key={item.id} item={item} index={index} onRefresh={fetchMeetingDetails} />
            ))}
          </div>
        )}
        </div>
      )}

      {/* Pre-Board Readiness Dashboard */}
      {activeTab === 'readiness' && readinessData && (
        <div className="space-y-6">
          {/* Readiness Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border-2 border-gray-300 p-4">
              <h4 className="text-sm font-medium text-gray-600 mb-1">Total Agenda Items</h4>
              <p className="text-3xl font-bold text-gray-900">{readinessData.total_agenda_items}</p>
            </div>
            <div className="bg-green-50 rounded-xl border-2 border-green-300 p-4">
              <h4 className="text-sm font-medium text-green-600 mb-1">Items Ready</h4>
              <p className="text-3xl font-bold text-green-900">{readinessData.items_ready}</p>
            </div>
            <div className="bg-red-50 rounded-xl border-2 border-red-300 p-4">
              <h4 className="text-sm font-medium text-red-600 mb-1">Items with Concerns</h4>
              <p className="text-3xl font-bold text-red-900">{readinessData.items_with_concerns}</p>
            </div>
            <div className="bg-blue-50 rounded-xl border-2 border-blue-300 p-4">
              <h4 className="text-sm font-medium text-blue-600 mb-1">Readiness</h4>
              <p className="text-3xl font-bold text-blue-900">{readinessData.readiness_percentage}%</p>
            </div>
          </div>

          {/* Readiness Table */}
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b-2 border-gray-200">
              <h3 className="font-bold text-gray-900">Agenda Item Readiness Status</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">#</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Agenda Item</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Category</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Docs</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Comments</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">❓</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">⚠️</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">📄</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {readinessData.agenda_readiness.map((item, index) => (
                    <tr key={item.agenda_item_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900 font-medium">#{index + 1}</td>
                      <td className="px-4 py-3 text-gray-900">{item.title}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.document_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.document_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          {item.comment_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{item.clarification_count}</td>
                      <td className="px-4 py-3 text-center text-sm">{item.risk_count}</td>
                      <td className="px-4 py-3 text-center text-sm">{item.info_request_count}</td>
                      <td className="px-4 py-3 text-center">
                        {item.ready ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            ✓ Ready
                          </span>
                        ) : item.has_concerns ? (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                            ⚠ Concerns
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Live Meeting Control */}
      {activeTab === 'live' && agendaItems.length > 0 && (
        <div className="space-y-6">
          {/* Current Agenda Display */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 text-white">
            <div className="text-center">
              <p className="text-sm text-green-100 mb-2">CURRENT AGENDA ITEM</p>
              <h2 className="text-3xl font-bold mb-4">
                #{currentAgendaIndex + 1}: {agendaItems[currentAgendaIndex]?.title}
              </h2>
              <div className="flex items-center justify-center gap-6 text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {agendaItems[currentAgendaIndex]?.planned_duration} min allocated
                </span>
                <span className="px-3 py-1 bg-white/20 rounded-full">
                  {agendaItems[currentAgendaIndex]?.item_type}
                </span>
                <span className="px-3 py-1 bg-white/20 rounded-full">
                  {agendaItems[currentAgendaIndex]?.category}
                </span>
              </div>
            </div>
          </div>

          {/* Meeting Controls */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Meeting Controls</h3>
            
            {/* Control Buttons */}
            {agendaItems.some(i => !['Completed', 'Deferred'].includes(i.status)) ? (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const duration = prompt('Enter actual duration (minutes):', agendaItems[currentAgendaIndex]?.planned_duration);
                    if (duration) markCurrentComplete(parseInt(duration));
                  }}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 justify-center"
                  disabled={currentAgendaIndex >= agendaItems.length}
                >
                  <CheckCircle className="w-5 h-5" />
                  Mark Complete & Next
                </button>
                <button
                  onClick={deferCurrentItem}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium flex items-center gap-2 justify-center"
                >
                  <AlertCircle className="w-5 h-5" />
                  Defer Item
                </button>
              </div>
            ) : (
              <button
                onClick={async () => {
                  const token = localStorage.getItem('token');
                  try {
                    await axios.put(
                      `${API}/api/boardos/meetings/${meeting.id}`,
                      { status: 'Completed' },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    showSuccess('Meeting completed successfully!');
                    fetchMeetingDetails();
                    setActiveTab('minutes');
                  } catch (error) {
                    showError('Error: ' + error.message);
                  }
                }}
                className="w-full px-8 py-6 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 font-bold text-xl flex items-center justify-center gap-3 shadow-lg"
              >
                <CheckCircle className="w-8 h-8" />
                End Meeting & Generate Minutes
              </button>
            )}
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Full Agenda List with Details */}
            <div className="lg:col-span-2 space-y-3">
              <h3 className="font-bold text-gray-900 mb-4">Full Agenda</h3>
              {agendaItems.map((item, index) => (
                <LiveAgendaTile 
                  key={item.id} 
                  item={item} 
                  index={index} 
                  isCurrent={item.status === 'Current'}
                />
              ))}
            </div>

            {/* Right: Stats & Recording */}
            <div className="space-y-6">
              {/* Meeting Statistics */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4">Statistics</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Completed:</span>
                    <span className="font-semibold text-green-700">
                      {agendaItems.filter(i => i.status === 'Completed').length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Deferred:</span>
                    <span className="font-semibold text-orange-700">
                      {agendaItems.filter(i => i.status === 'Deferred').length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Remaining:</span>
                    <span className="font-semibold text-gray-700">
                      {agendaItems.filter(i => !['Completed', 'Deferred'].includes(i.status)).length}
                    </span>
                  </div>
                  <div className="pt-3 border-t border-gray-200 mt-3">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Time Tracking</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Planned:</span>
                        <span className="font-semibold text-blue-700">
                          {agendaItems.reduce((sum, i) => sum + i.planned_duration, 0)} min
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Actual:</span>
                        <span className="font-semibold text-green-700">
                          {agendaItems.reduce((sum, i) => sum + (i.actual_duration || 0), 0)} min
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Meeting Recording */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                
                {transcript && transcript.transcript_text ? (
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-green-900 mb-1">
                          Recording Transcribed ✓
                        </p>
                        <p className="text-sm text-green-800">
                          {transcript.source_file || 'Manual transcript'}
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          {transcript.transcript_text?.length || 0} characters transcribed
                        </p>
                        <p className="text-xs text-green-600 mt-2">
                          This transcript will be used to generate accurate minutes
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-purple-900 mb-3">
                        <strong>📹 Upload Recording:</strong> Audio/video will be transcribed using AI (Whisper) for accurate minutes
                      </p>
                      <label className={`w-full px-6 py-3 rounded-lg font-medium flex items-center gap-2 justify-center cursor-pointer transition-all ${
                        uploadingRecording 
                          ? 'bg-gray-400 text-white' 
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}>
                        {uploadingRecording ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Transcribing...
                          </>
                        ) : (
                          <>
                            <Video className="w-5 h-5" />
                            Upload Recording (Audio/Video)
                          </>
                        )}
                        <input
                          type="file"
                          accept="audio/*,video/*,.mp3,.mp4,.wav,.m4a,.webm"
                          onChange={handleRecordingUpload}
                          className="hidden"
                          disabled={uploadingRecording}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Supported: MP3, MP4, WAV, M4A, WebM
                    </p>
                    <div className="text-center text-sm text-gray-600 my-2">OR</div>
                    <button
                      onClick={() => setShowTranscriptText(!showTranscriptText)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      {showTranscriptText ? 'Hide' : 'Upload Text Transcript'}
                    </button>
                    
                    {showTranscriptText && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          placeholder="Paste meeting transcript text here..."
                          rows={6}
                          id="transcript-text-input"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          onClick={async () => {
                            const text = document.getElementById('transcript-text-input').value;
                            if (!text.trim()) {
                              showWarning('Please enter transcript text');
                              return;
                            }

                            const token = localStorage.getItem('token');
                            try {
                              await axios.post(
                                `${API}/api/boardos/meetings/${meeting.id}/transcript`,
                                { meeting_id: meeting.id, transcript_text: text, auto_segment: false },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              showSuccess('Transcript uploaded successfully!');
                              setTranscript({ transcript_text: text });
                              setShowTranscriptText(false);
                              fetchMeetingDetails();
                            } catch (error) {
                              showError('Error: ' + error.message);
                            }
                          }}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Save Transcript
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minutes View */}
      {activeTab === 'minutes' && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Board Meeting Minutes</h3>
          
          {!minutes || !minutes.content ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-2">No Minutes Generated Yet</h4>
              <p className="text-gray-600 mb-6">
                {meetingData.status === 'In Progress' || meetingData.status === 'Completed'
                  ? 'Generate AI-powered draft minutes from meeting data'
                  : 'Complete the meeting first to generate minutes'}
              </p>
              {(meetingData.status === 'In Progress' || meetingData.status === 'Completed') && (
                <button
                  onClick={generateMinutes}
                  disabled={generatingMinutes}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Sparkles className="w-5 h-5" />
                  {generatingMinutes ? 'Generating Minutes...' : 'Generate Minutes (AI)'}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    minutes.status === 'Approved' ? 'bg-green-100 text-green-700' :
                    minutes.status === 'Under Review' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {minutes.status || 'Draft'}
                  </span>
                  <span className="text-sm text-gray-600">
                    Version {minutes.version || 1}
                  </span>
                  <span className="text-sm text-gray-600">
                    Generated: {new Date(minutes.generated_at).toLocaleString()}
                  </span>
                </div>
                {minutes.status === 'Draft' && (
                  <button
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      try {
                        await axios.put(`${API}/api/boardos/minutes/${minutes.id}/approve`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        showSuccess('Minutes approved!');
                        fetchMeetingDetails();
                      } catch (error) {
                        showError('Error: ' + error.message);
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Approve Minutes
                  </button>
                )}
              </div>

              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap">
                  {minutes.content}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions View */}
      {activeTab === 'actions' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Action Items</h3>
            <button
              onClick={() => setShowAddAction(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Action Item
            </button>
          </div>

          {actionItems.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <ListChecks className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-900 mb-2">No Action Items Yet</h4>
              <p className="text-gray-600">
                Action items will be extracted from approved minutes or added manually
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Owner</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Due Date</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Updates</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {actionItems.map((action) => (
                    <tr key={action.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{action.description}</td>
                      <td className="px-4 py-3 text-gray-700">{action.owner}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm ${action.is_overdue ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                          {new Date(action.due_date).toLocaleDateString()}
                          {action.is_overdue && ' ⚠️'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          action.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          action.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                          action.is_overdue ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {action.is_overdue && action.status === 'Open' ? 'Overdue' : action.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {action.progress_updates?.length || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Agenda Modal */}
      {showAddAgenda && (
        <AddAgendaModal
          meetingId={meetingData.id}
          orderIndex={agendaItems.length}
          onClose={() => setShowAddAgenda(false)}
          onSuccess={() => { setShowAddAgenda(false); fetchMeetingDetails(); }}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />
      )}

      {/* Add Action Modal */}
      {showAddAction && (
        <AddActionModal
          meetingId={meeting.id}
          onClose={() => setShowAddAction(false)}
          onSuccess={() => { setShowAddAction(false); fetchMeetingDetails(); }}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />
      )}
    </div>
  );
};

// ==================== AGENDA TILE COMPONENT ====================
const AgendaTile = ({ item, index, onRefresh }) => {
  const { showSuccess, showError } = useAlert();
  const [expanded, setExpanded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [uploading, setUploading] = useState(false);

  const getTypeColor = (type) => {
    if (type === 'Approval') return 'bg-red-100 text-red-700';
    if (type === 'Review') return 'bg-blue-100 text-blue-700';
    if (type === 'Discussion') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getCommentIcon = (type) => {
    if (type === 'Clarification') return '❓';
    if (type === 'Risk') return '⚠️';
    if (type === 'Suggestion') return '📌';
    if (type === 'InfoRequest') return '📄';
    return '👍';
  };

  const fetchDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const [docsRes, commentsRes] = await Promise.all([
        axios.get(`${API}/api/boardos/agenda-items/${item.id}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/boardos/agenda-items/${item.id}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDocuments(docsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching details:', error);
    }
  };

  const handleExpand = () => {
    if (!expanded) {
      fetchDetails();
    }
    setExpanded(!expanded);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('agenda_item_id', item.id);
    formData.append('is_mandatory', 'true');

    try {
      await axios.post(`${API}/api/boardos/documents/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      showSuccess('Document uploaded successfully!');
      fetchDetails();
      onRefresh();
    } catch (error) {
      showError('Error uploading: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-2 border-gray-200 rounded-lg hover:border-indigo-300 transition-all">
      {/* Main Tile */}
      <div className="p-4 cursor-pointer" onClick={handleExpand}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-3 flex-1">
            <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">{item.title}</h4>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              )}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(item.item_type)}`}>
            {item.item_type}
          </span>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {item.planned_duration} min
            </span>
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              {item.category}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {item.document_count || 0} docs
            </span>
            <span className="flex items-center gap-1">
              💬 {item.comment_count || 0} comments
            </span>
          </div>
          <button className="text-xs text-indigo-600 font-medium">
            {expanded ? '▲ Collapse' : '▼ Expand'}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t-2 border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            {/* Documents Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-gray-900">Documents</h5>
                <label className="px-3 py-1 bg-indigo-600 text-white rounded text-xs cursor-pointer hover:bg-indigo-700">
                  {uploading ? 'Uploading...' : '+ Upload'}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              {documents.length === 0 ? (
                <p className="text-sm text-gray-500">No documents uploaded yet</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-white border border-gray-200 rounded p-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-900 truncate">{doc.file_name}</span>
                        {doc.is_mandatory && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Mandatory</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(doc.uploaded_at).toLocaleDateString()} by {doc.uploaded_by_email}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comments Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-semibold text-gray-900">Pre-Board Comments</h5>
                <button
                  onClick={() => setShowAddComment(true)}
                  className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                >
                  + Add Comment
                </button>
              </div>
              {comments.length === 0 ? (
                <p className="text-sm text-gray-500">No comments yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {comments.map((comment) => (
                    <div key={comment.id} className="bg-white border border-gray-200 rounded p-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{getCommentIcon(comment.comment_type)}</span>
                        <div className="flex-1">
                          <p className="text-gray-900">{comment.comment_text}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {comment.user_name} • {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add Comment Modal */}
          {showAddComment && (
            <AddCommentModal
              agendaItemId={item.id}
              onClose={() => setShowAddComment(false)}
              onSuccess={() => { setShowAddComment(false); fetchDetails(); onRefresh(); }}
              showSuccess={showSuccess}
              showError={showError}
              showWarning={showWarning}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ==================== ADD AGENDA MODAL ====================
const AddAgendaModal = ({ meetingId, orderIndex, onClose, onSuccess, showSuccess, showError, showWarning }) => {
  const [formData, setFormData] = useState({
    meeting_id: meetingId,
    title: '',
    description: '',
    category: 'Strategic',
    item_type: 'Discussion',
    planned_duration: 15,
    order_index: orderIndex
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title) {
      showWarning('Please enter agenda item title');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/boardos/agenda-items`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Agenda item added successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Add Agenda Item</h3>
          <button onClick={onClose} className="text-white hover:bg-indigo-700 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agenda Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Approval of Q4 Financial Statements"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional context or background..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Financial">Financial</option>
                <option value="Strategic">Strategic</option>
                <option value="Operational">Operational</option>
                <option value="Compliance">Compliance</option>
                <option value="Governance">Governance</option>
                <option value="Risk">Risk</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
              <select
                value={formData.item_type}
                onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Approval">Approval</option>
                <option value="Review">Review</option>
                <option value="Discussion">Discussion</option>
                <option value="Noting">Noting</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Planned Duration (minutes)</label>
            <input
              type="number"
              value={formData.planned_duration}
              onChange={(e) => setFormData({ ...formData, planned_duration: parseInt(e.target.value) })}
              min="5"
              step="5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Agenda Item'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ADD COMMENT MODAL ====================
const AddCommentModal = ({ agendaItemId, onClose, onSuccess, showSuccess, showError, showWarning }) => {
  const [formData, setFormData] = useState({
    agenda_item_id: agendaItemId,
    comment_text: '',
    comment_type: 'Clarification',
    visible_to_all: false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.comment_text.trim()) {
      showWarning('Please enter a comment');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/boardos/comments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Comment added successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
        <div className="bg-purple-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Add Pre-Board Comment</h3>
          <button onClick={onClose} className="text-white hover:bg-purple-700 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comment Type</label>
            <select
              value={formData.comment_type}
              onChange={(e) => setFormData({ ...formData, comment_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Clarification">❓ Clarification - Seeking clarity</option>
              <option value="Risk">⚠️ Risk - Highlighting potential issues</option>
              <option value="Suggestion">📌 Suggestion - Proposing alternatives</option>
              <option value="InfoRequest">📄 Info Request - Additional data needed</option>
              <option value="NoConcern">👍 No Concern - All clear</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Comment</label>
            <textarea
              value={formData.comment_text}
              onChange={(e) => setFormData({ ...formData, comment_text: e.target.value })}
              placeholder="Enter your comment or question..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.visible_to_all}
              onChange={(e) => setFormData({ ...formData, visible_to_all: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm text-gray-700">Make visible to all board members (default: CS/Management only)</label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Comment'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ADD ACTION MODAL ====================
const AddActionModal = ({ meetingId, onClose, onSuccess, showSuccess, showError, showWarning }) => {
  const [formData, setFormData] = useState({
    meeting_id: meetingId,
    description: '',
    owner: '',
    due_date: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.description || !formData.owner || !formData.due_date) {
      showWarning('Please fill in all required fields');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/api/boardos/action-items`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Action item created successfully!');
      onSuccess();
    } catch (error) {
      showError('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full">
        <div className="bg-orange-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Add Action Item</h3>
          <button onClick={onClose} className="text-white hover:bg-orange-700 rounded p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What needs to be done?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner *</label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="Name or role"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Action Item'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default BoardProcessModule;


// ==================== LIVE AGENDA TILE (With Full Details) ====================
const LiveAgendaTile = ({ item, index, isCurrent }) => {
  const [expanded, setExpanded] = useState(true); // Expanded by default
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    if (expanded || isCurrent) {
      fetchDetails();
    }
  }, [expanded, isCurrent, item.id]);

  const fetchDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const [docsRes, commentsRes] = await Promise.all([
        axios.get(`${API}/api/boardos/agenda-items/${item.id}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/boardos/agenda-items/${item.id}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDocuments(docsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching details:', error);
    }
  };

  const getCommentIcon = (type) => {
    if (type === 'Clarification') return '❓';
    if (type === 'Risk') return '⚠️';
    if (type === 'Suggestion') return '📌';
    if (type === 'InfoRequest') return '📄';
    return '👍';
  };

  return (
    <div className={`border-2 rounded-lg transition-all ${
      item.status === 'Current' 
        ? 'border-green-500 bg-green-50' 
        : item.status === 'Completed'
        ? 'border-gray-300 bg-gray-50'
        : item.status === 'Deferred'
        ? 'border-orange-300 bg-orange-50'
        : 'border-gray-200 bg-white'
    }`}>
      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {item.status === 'Current' && (
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            )}
            {item.status === 'Completed' && (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            {item.status === 'Deferred' && (
              <AlertCircle className="w-5 h-5 text-orange-600" />
            )}
            <div>
              <h4 className="font-semibold text-gray-900">#{index + 1}. {item.title}</h4>
              <p className="text-xs text-gray-600 mt-1">
                {item.planned_duration} min • {item.item_type} • {item.category}
              </p>
            </div>
          </div>
          <button className="text-xs text-indigo-600 font-medium">
            {expanded ? '▲ Collapse' : '▼ Expand'}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t-2 border-gray-200 p-4 bg-white/50">
          {/* Description - Always show */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
            <p className="text-sm text-gray-900">{item.description || 'No description provided'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Documents */}
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Documents ({documents.length})</p>
              {documents.length === 0 ? (
                <p className="text-xs text-gray-500">No documents</p>
              ) : (
                <div className="space-y-1">
                  {documents.map((doc) => (
                    <div key={doc.id} className="bg-white border border-gray-200 rounded p-2">
                      <p className="text-xs font-medium text-gray-900 truncate">{doc.file_name}</p>
                      <p className="text-xs text-gray-500">{doc.is_mandatory && '🔴 Mandatory'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comments */}
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Comments ({comments.length})</p>
              {comments.length === 0 ? (
                <p className="text-xs text-gray-500">No comments</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {comments.map((comment) => (
                    <div key={comment.id} className="bg-white border border-gray-200 rounded p-2">
                      <div className="flex items-start gap-1">
                        <span className="text-sm">{getCommentIcon(comment.comment_type)}</span>
                        <p className="text-xs text-gray-900 flex-1">{comment.comment_text}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{comment.user_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

